import sortBy from 'lodash/sortBy';

interface Range {
  from: number;
  to: number;
}

/**
 * Given a list of range objects of the form { from: Number, to: Number },
 * this function sorts those ranges by `from` and merges ranges that overlap
 **/
export const sortAndMergeRanges = (ranges: Range[] = []): Range[] => {
  const sortedRanges = sortBy(ranges, range => range.from);

  const mergedRanges = sortedRanges.reduce((merged: Range[], range, ind) => {
    if (ind === 0) {
      return merged.concat(range);
    }

    const lastInd = merged.length - 1;
    const previousRange = merged[lastInd];

    if (previousRange == null || previousRange.to < range.from) {
      return merged.concat(range);
    }

    merged[lastInd] = {
      from: Math.min(previousRange.from, range.from),
      to: Math.max(previousRange.to, range.to),
    };

    return merged;
  }, []);

  return mergedRanges;
};

/**
 * Given a list of range objects of the form { from: Number, to: Number },
 * this function sorts those ranges by `from`, merges the ranges that overlap,
 * and then finds the compliment ranges within `minValue` and `maxValue`. These
 * complement ranges can have a padding with the original ranges provided so the
 * complements do not overlap at all.
 *
 * For more on complements:
 * https://en.wikipedia.org/wiki/Complement_(set_theory)
 **/
export const getComplementRanges = (
  ranges: Range[],
  minValue: number,
  maxValue: number,
  padding = 0
): Range[] => {
  const sortedAndMerged = sortAndMergeRanges(ranges);

  let currentStart = minValue;

  const complementRanges: Range[] = [];

  sortedAndMerged.forEach(range => {
    // if there is a range before this one begins, add that as a complement
    if (currentStart < range.from) {
      complementRanges.push({
        from: Math.max(currentStart, minValue),
        to: Math.min(range.from, maxValue) - padding,
      });
    }

    currentStart = range.to + padding;
  });

  if (currentStart < maxValue) {
    complementRanges.push({
      from: currentStart,
      to: maxValue,
    });
  }

  return complementRanges;
};

export const areRangesDisjoint = (rangeA: Range, rangeB: Range): boolean => {
  return rangeA.from >= rangeB.to || rangeB.from >= rangeA.to;
};

const doesRangeContain = (rangeA, rangeB) =>
  rangeA.from <= rangeB.from && rangeA.to >= rangeB.to;

// must return an array of ranges because a range may be split
export const getRangeDifference = (rangeA: Range, rangeB: Range): Range[] => {
  if (areRangesDisjoint(rangeA, rangeB)) {
    return [rangeA];
  }

  if (doesRangeContain(rangeA, rangeB)) {
    return [
      { from: rangeA.from, to: rangeB.from },
      { from: rangeB.to, to: rangeA.to },
    ];
  }

  if (doesRangeContain(rangeB, rangeA)) {
    return [];
  }

  if (rangeA.from <= rangeB.from) {
    return [{ from: rangeA.from, to: rangeB.from }];
  }

  return [
    {
      from: rangeB.to,
      to: rangeA.to,
    },
  ];
};

// This function takes an array of rangesA and removes all overlaps with rangesB
// from rangesA.
// Is there a way to do this that isn't O(n^2)?
export const removeRangeOverlaps = (
  rangesA: Range[],
  rangesB: Range[]
): Range[] => {
  let newRanges = [...rangesA];

  for (const removedRange of rangesB) {
    newRanges = newRanges.reduce(
      (acc: Range[], range) =>
        acc.concat(getRangeDifference(range, removedRange)),
      []
    );
  }

  return newRanges;
};

export const isNumberInRanges = (ranges: Range[], number: number): boolean => {
  for (const range of ranges) {
    if (range.from <= number && range.to >= number) {
      return true;
    }
  }

  return false;
};

const rangeLength = ({ from, to }) => to - from;
const shiftRange = ({ from, to }, delta) => ({
  from: from + delta,
  to: to + delta,
});

/*
 * When we have a video with boundaries, we want to present it to the user as
 * an unbroken clip. The user can trim that clip. Those trims are relative to
 * the unbroken boundaried clip, but we need to be able to calculate them as
 * absolute ranges in the context of the unboundaried clip.
 *
 * Example:
 * Here we have a clip of duration 100 with boundaries
 * [{from: 0, to: 30}, {from: 80, to: 100}].
 *
 * When presented to the user, it is a clip of duration 50.
 * The user adds a trim range of {from: 40, to: 50}.
 *
 * Upon save, this clip has boundaries of
 * [{from: 0, to: 30}, {from: 80, to: 100}]
 * and trim ranges of
 * [{from: 70, to: 80}].

  Original clip

       Boundaries
      ┌──────────┐                  ┌───────┐
      └──────────┴──────────────────┴───────┘
      0          30                 80     100
                           │
                           │
                           │
     Boundaried clip       │
                           ▼        Relative trim range
                              ┌─────┐
                 ─────────────┴─────┘
                0             40    50

   Saved clip
                              ┌─────┐Absolute trim range
        Boundaries            └─────┤
       ┌──────────┐                 ├──────────┐
       └──────────┴─────────────────┴──────────┘
       0         30           70    80        100
 *
 */
export const convertRangesFromRelativeToAbsolute = (
  baseRanges: Range[],
  relativeRanges: Range[]
): Range[] => {
  let absoluteRanges: Range[] = [];

  // Copy and sort in ascending order
  baseRanges = baseRanges.slice();
  baseRanges.sort((a, b) => a.from - b.from);

  for (let rRange of relativeRanges) {
    baseRanges.forEach(bRange => {
      if (bRange.from <= rRange.from) {
        rRange = shiftRange(rRange, rangeLength(bRange));
      }
    });

    const intersectingRanges = baseRanges.filter(
      bRange => !areRangesDisjoint(bRange, rRange)
    );

    if (intersectingRanges.length) {
      const splitRanges: Range[] = [];
      let lastSplitRange = rRange;

      for (const iRange of intersectingRanges) {
        splitRanges.push({ from: lastSplitRange.from, to: iRange.from });
        lastSplitRange = {
          from: iRange.to,
          to: lastSplitRange.to + rangeLength(iRange),
        };
      }

      splitRanges.push(lastSplitRange);

      absoluteRanges = absoluteRanges.concat(splitRanges);
    } else {
      absoluteRanges.push(rRange);
    }
  }

  return absoluteRanges;
};

/*
 * When we have a video with boundaries, we want to present it to the user as
 * an unbroken clip. The user can trim that clip. Those trims are stored as
 * absolute ranges on the full video, but we need to be able to calculate them
 * as relative ranges in the context of the boundaried clip.
 *
 * Example:
 * Here we have a clip of duration 100 with boundaries
 * [{from: 0, to: 30}, {from: 80, to: 100}]
 * and trim ranges of
 * [{from: 70, to: 80}].
 *
 * When presented to the user, it is a clip of duration 50
 * with trim ranges of {from: 40, to: 50}.
 *
   Saved clip
                              ┌─────┐Absolute trim range
        Boundaries            └─────┤
       ┌──────────┐                 ├──────────┐
       └──────────┴─────────────────┴──────────┘
       0         30           70    80        100
                           │
                           │
                           │
     Boundaried clip       │
                           ▼        Relative trim range
                              ┌─────┐
                 ─────────────┴─────┘
                0             40    50
 */

export const convertRangesFromAbsoluteToRelative = (
  baseRanges: Range[],
  absoluteRanges: Range[]
): Range[] => {
  const relativeRanges: Range[] = [];

  // Copy and sort in descending order
  baseRanges = baseRanges.slice();
  baseRanges.sort((a, b) => b.from - a.from);

  for (let aRange of absoluteRanges) {
    baseRanges.forEach(bRange => {
      if (bRange.to <= aRange.from) {
        aRange = shiftRange(aRange, -rangeLength(bRange));
      }
    });

    relativeRanges.push(aRange);
  }

  return sortAndMergeRanges(relativeRanges);
};
